import { FC, useEffect, useMemo, useState } from 'react';

import { useCityStore } from '@store/city';

import { type CityTabId } from '../../types';
import CitySelector from './components/CitySelector';
import OfficeCard from './components/OfficeCard';
import OfficeMap from './components/OfficeMap';
import styles from './RegionalOfficesSection.module.scss';
import { CityOption, RegionalOfficesSectionProps } from './types';
import { isTrustedYandexMapWidgetSrc, resolveDefaultCityId } from './utils';

const RegionalOfficesSection: FC<RegionalOfficesSectionProps> = ({ offices, hideTitle = false }) => {
    const userCityName = useCityStore(({ currentCity }) => currentCity.city);
    const defaultCityId = useMemo(() => resolveDefaultCityId(userCityName, offices), [userCityName, offices]);
    const [activeCity, setActiveCity] = useState<CityTabId>(defaultCityId);
    const [loadedCities, setLoadedCities] = useState<Set<CityTabId>>(() => new Set<CityTabId>([defaultCityId]));
    const [isCityPopupOpen, setIsCityPopupOpen] = useState(false);
    const office = useMemo(() => offices.find(city => city.id === activeCity), [activeCity, offices]);

    const cityOptions = useMemo<CityOption[]>(
        () =>
            offices.map(city => ({
                id: city.id,
                label: city.label,
                isActive: city.id === activeCity,
            })),
        [activeCity, offices]
    );

    const mapWidgetSrcByCity = useMemo(() => {
        return new Map(
            offices.map(city => {
                const mapQuery = city.sales.address || '';
                const customMapSrc = city.sales.yandexMapWidgetSrc?.trim() ?? '';
                const mapSrc =
                    customMapSrc && isTrustedYandexMapWidgetSrc(customMapSrc)
                        ? customMapSrc
                        : mapQuery
                          ? `https://yandex.ru/map-widget/v1/?text=${encodeURIComponent(mapQuery)}&z=14`
                          : '';

                return [city.id, mapSrc];
            })
        );
    }, [offices]);

    const handleSelectCity = (cityId: CityTabId) => {
        setLoadedCities(previous => {
            if (previous.has(cityId)) {
                return previous;
            }

            const next = new Set(previous);
            next.add(cityId);

            return next;
        });
        setActiveCity(cityId);
        setIsCityPopupOpen(false);
    };

    const sectionClassName = hideTitle
        ? `${styles.regionalOffices_section} ${styles.regionalOffices_sectionCompact}`
        : styles.regionalOffices_section;

    useEffect(() => {
        setActiveCity(defaultCityId);
        setLoadedCities(previous => {
            if (previous.has(defaultCityId)) {
                return previous;
            }

            const next = new Set(previous);
            next.add(defaultCityId);

            return next;
        });
    }, [defaultCityId]);

    return (
        <section className={sectionClassName} aria-labelledby="contacts-regional-heading">
            {!hideTitle && (
                <h2 id="contacts-regional-heading" className={styles.regionalOffices_sectionTitle}>
                    Региональные офисы и склады
                </h2>
            )}
            <CitySelector
                cityOptions={cityOptions}
                isCityPopupOpen={isCityPopupOpen}
                onOpen={() => setIsCityPopupOpen(true)}
                onClose={() => setIsCityPopupOpen(false)}
                onSelect={handleSelectCity}
            />
            {office && (
                <div className={styles.regionalOffices_officeRow}>
                    <OfficeCard
                        title={office.sales.title}
                        schedule={office.sales.schedule}
                        address={office.sales.address}
                        phoneLabel={office.sales.phoneLabel}
                        phoneTel={office.sales.phoneTel}
                        email={office.sales.email}
                        isCompact={hideTitle}
                    />
                    {office.warehouse && (
                        <OfficeCard
                            title={office.warehouse.title}
                            schedule={office.warehouse.schedule}
                            address={office.warehouse.address}
                            phoneLabel={office.warehouse.phoneLabel}
                            phoneTel={office.warehouse.phoneTel}
                            email={office.warehouse.email}
                            routeSchemeUrl={office.warehouse.routeSchemeUrl}
                            isWarehouse
                            isCompact={hideTitle}
                        />
                    )}
                    <OfficeMap
                        offices={offices}
                        loadedCities={loadedCities}
                        activeCity={activeCity}
                        mapWidgetSrcByCity={mapWidgetSrcByCity}
                        isCompact={hideTitle}
                    />
                </div>
            )}
        </section>
    );
};

export default RegionalOfficesSection;
